import { BillDocumentType as BillGridDocumentType } from "@btnet/services-btnet/handlers/models/billDocumentType";

import { BillDocumentType } from "entity/bill/Bill/Bill.api.types";

export type BillToastAction =
    | "markedReady"
    | "unmarkedReady"
    | "saved"
    | "savedAsDraft"
    | "sentForApproval"
    | "voided"
    | "deleted"
    | "approvedAndNext"
    | "approved";

const billMessages: Record<BillToastAction, (accountingUpdatedMessage: string) => string> = {
    markedReady: (suffix) => `Bill successfully marked ready for payment${suffix}.`,
    unmarkedReady: (suffix) => `Bill successfully unmarked ready for payment${suffix}.`,
    saved: (suffix) => `Bill saved${suffix}`,
    savedAsDraft: () => "Bill saved as draft",
    sentForApproval: () => "Bill sent for approval. Approvers will be notified.",
    voided: () => "Bill voided",
    deleted: () => "Bill deleted",
    approvedAndNext: () => "Bill approved. Opening next bill",
    approved: () => "Bill approved",
};

const vendorCreditMessages: Record<BillToastAction, (accountingUpdatedMessage: string) => string> =
    {
        markedReady: (suffix) => `Vendor credit marked available${suffix}.`,
        unmarkedReady: (suffix) => `Vendor credit unmarked available${suffix}.`,
        saved: (suffix) => `Vendor credit saved${suffix}.`,
        savedAsDraft: () => "Vendor credit saved as draft",
        sentForApproval: () => "Vendor credit sent for approval. Approvers will be notified.",
        voided: () => "Vendor credit voided",
        deleted: () => "Vendor credit deleted",
        approvedAndNext: () => "Vendor credit approved. Opening next vendor credit",
        approved: () => "Vendor credit approved",
    };

export const getBillToastMessage = (
    action: BillToastAction,
    documentType: BillDocumentType | undefined,
    accountingUpdatedMessage = "",
): string => {
    const isVendorCredit = documentType === BillDocumentType.VendorCredit;
    const messages = isVendorCredit ? vendorCreditMessages : billMessages;
    return messages[action](accountingUpdatedMessage);
};

// Bulk-action toast wording. This map only owns the noun phrase (String literal).
// The bulk-approve flow lives in the performBulkApprove helper (shared by the BillList FAB
// and the approval-queue page), so "approved" here is its wording source, not a duplicate of it.
export type BillBulkToastAction =
    | "markedReady"
    | "approved"
    | "sentForApproval"
    | "voided"
    | "deleted"
    | "voidNoneEligible";

const billBulkSummaries: Record<BillBulkToastAction, string> = {
    markedReady: "bills marked ready for payment",
    approved: "bills approved",
    sentForApproval: "bills sent for approval",
    voided: "bills voided",
    deleted: "bills deleted",
    voidNoneEligible: "All selected bills are already voided or processing a payment",
};

const vendorCreditBulkSummaries: Record<BillBulkToastAction, string> = {
    markedReady: "vendor credits marked available",
    approved: "vendor credits approved",
    sentForApproval: "vendor credits sent for approval",
    voided: "vendor credits voided",
    deleted: "vendor credits deleted",
    voidNoneEligible: "All selected vendor credits are already voided or processing a payment",
};

export const getBillBulkSummary = (
    action: BillBulkToastAction,
    mainDocType: BillGridDocumentType | undefined,
): string => {
    const useVendorCredits = mainDocType === BillGridDocumentType.VendorCredit;
    return (useVendorCredits ? vendorCreditBulkSummaries : billBulkSummaries)[action];
};
